import type { IApiResponse } from '@/interfaces/IApiResponse'
import type { FullBalance } from '@/interfaces/IFullBalance'
import { amountHelper } from '@nami/domain/classes'
import { toRefs } from 'vue'
import useAxios from '@/composable/useAxios'
import { useGlobalStore } from '@/stores/globalStore'
import { useUserStore } from '@/stores/userStore'

function getBalanceFromBalanceApi(): Promise<number> {
    return useAxios.get<number>('/balance', { noLoading: true }).then(response => response.data)
}

export const getBonusWalletDetailApi = {
    get: async (): Promise<FullWalletInfo> => {
        const response = await useAxios.get<IApiResponse<FullWalletInfo>>('/bonus-wallet-detail', {
            noLoading: true,
        })
        return response.data.data
    },
}

function getBalanceByToggle(useAccountBalance: boolean): Promise<number> {
    if (useAccountBalance) {
        return getBonusWalletDetailApi.get().then((fullWalletInfo) => {
            const totalBonusBalance = fullWalletInfo.bonusWallets.reduce(
                (sum, wallet) => sum + wallet.walletBalance,
                0,
            )
            return fullWalletInfo.mainWalletBalance + totalBonusBalance
        })
    }
    return getBalanceFromBalanceApi()
}

export const balanceApi = {
    get: async (): Promise<string> => {
        const globalStore = useGlobalStore()
        const { userInfo } = toRefs(useUserStore())
        const displayBonusWalletAmount = globalStore.mainToggle.toggleDisplayBonusWalletAmount
        const balanceNumber = await getBalanceByToggle(displayBonusWalletAmount)
        globalStore.setBalanceNumber(balanceNumber)
        globalStore.setBalance(amountHelper.getLocaleStringWithSymbol(balanceNumber, userInfo.value.currency.toString(), 2))

        return globalStore.balanceDisplay
    },
}

export const withdrawableAmountApi = {
    get: async (): Promise<number> => {
        const { data } = await useAxios.get<WithdrawAmountResponse>('/v2/withdrawable-amount', { noLoading: true })
        const globalStore = useGlobalStore()
        globalStore.setWithdrawableAmount(data.withdrawableAmount)
        globalStore.setLastUpdateTime(data.betCheckTime)
        return globalStore.withdrawableAmountNumber
    },
}

export const refreshWithdrawableAmountApi = {
    post: async (): Promise<void> => {
        await useAxios.post('/refresh-withdrawable-amount', { noLoading: true })
    },
}

export interface WithdrawAmountResponse {
    withdrawableAmount: number
    betCheckTime: Date
}

export interface BonusWalletModel {
    promotionTitle: Record<string, string>
    walletBalance: number
    currentTurnover: number
    targetTurnover: number
}

export interface FullWalletInfo {
    mainWalletBalance: number
    bonusWallets: BonusWalletModel[]
}

export const accountBalanceApi = {
    get: async (): Promise<FullBalance> => {
        const {
            data: { data },
        } = await useAxios.get<IApiResponse<FullBalance>>('/v2/account-balance', { noLoading: true })
        return data
    },
}
